import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ExistingCompany } from '~/components/@sibill-components/AutomaticallyAddSecondCompany/CheckCompany';
import { CompanyData } from '~/components/@sibill-components/AutomaticallyAddSecondCompany/CreateCompanyForm';
import Button from '~/components/buttons/NewButton';
import AlertMessage from '~/components/data-display/AlertMessage';
import { HStack, VStack } from '~/components/layout/Stack';
import ModalActions from '~/components/modals/Modal/ModalActions';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompany } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import IconArrowRight from '~/icons/icon-arrow-right.svg?react';
import IconSend from '~/icons/icon-send.svg?react';
import { ModalProps } from '~/modals/Modal';
import { RouteConfig } from '~/routes/routeConfig';
import { AnalyticsEvents } from '~/services/analytics';
import { useSecondCompanyCreateMutation } from '~/services/company';
import { useCreateSubscription, useNavigateToSecondCompanyCustomerPortal } from '~/services/subscription';
import { FeatureFlag, FiscalRegime, UserRole } from '~/types/company';
import { NewSecondCompanySubscriptionInfoResponse } from '~/types/subscriptions';
import { UserQueryKeys } from '~/types/user';
import { fDecimalNumberTwoDecimal } from '~/utils/formatNumber';
import { formatMoney, newMoney } from '~/utils/money';
import styles from './SubscribeCompany.module.scss';

interface AddCompanySearchProps extends ModalProps {
  companyData: ExistingCompany | CompanyData;
  subscriptionInfo?: NewSecondCompanySubscriptionInfoResponse;
}

const SubscribeCompany: React.FC<AddCompanySearchProps> = ({ closeModal, companyData, subscriptionInfo }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'add_company.steps.subscribe' });
  const companyCreateMutation = useSecondCompanyCreateMutation();
  const createSubscription = useCreateSubscription();
  const { captureException } = useExceptionLogger();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const currentCompany = useActiveCompany();
  const navigateToSecondCompanyCustomerPortal = useNavigateToSecondCompanyCustomerPortal();
  const { track } = useAnalytics();

  const createCompany = useCallback(async () => {
    try {
      if ('companyIdentityId' in companyData) {
        const { companyIdentityId, ...others } = companyData;

        return await companyCreateMutation.mutateAsync({
          ...others,
          currentCompanyUuid: currentCompany.id,
          fiscalRegime: FiscalRegime.RF01,
          companyIdentity: { id: companyIdentityId },
        });
      } else {
        return await companyCreateMutation.mutateAsync({
          currentCompanyUuid: currentCompany.id,
          name: companyData.name,
          vatNumber: companyData.vatNumber,
          taxNumber: companyData.taxNumber,
          fiscalRegime: companyData.fiscalRegime,
          userRole: UserRole.ENTREPRENEUR,
          companyIdentity: {
            lid: 'fake-id',
            address: companyData.address,
            city: companyData.city,
            country: companyData.country,
            addressCountry: companyData.country,
            postalCode: companyData.postalCode,
            vatNumber: companyData.vatNumber,
            taxNumber: companyData.taxNumber,
            companyName: companyData.name,
          },
        });
      }
    } catch (error) {
      if ('companyIdentityId' in companyData) {
        captureException(error, {
          extra: {
            company_name: companyData.name,
            vat_number: companyData.vatNumber,
            tax_number: companyData.taxNumber,
            company_identity_id: companyData.companyIdentityId,
          },
        });
      } else {
        captureException(error, {
          extra: {
            company_name: companyData.name,
            vat_number: companyData.vatNumber,
            tax_number: companyData.taxNumber,

            address: companyData.address,
            city: companyData.city,
            country: companyData.country,
            addressCountry: companyData.country,
            postalCode: companyData.postalCode,
          },
        });
      }
      return undefined;
    }
  }, [companyData, companyCreateMutation, currentCompany.id, captureException]);

  const handleCreateSubscription = useCallback(async () => {
    setIsLoading(true);
    if (!subscriptionInfo) {
      track(AnalyticsEvents.SECOND_COMPANIES_CREATION_STARTED, {
        step: 'subscription_activation_requested',
        metadata: { tax_code: companyData.taxNumber },
      });
      await createCompany();
      snack.success(t('messages.REQUEST_COMPANY_SUBSCRIPTION'));
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      closeModal();
      setIsLoading(false);
    } else {
      track(AnalyticsEvents.SECOND_COMPANIES_CREATION_STARTED, {
        step: 'subscription_activated',
        metadata: {
          tax_code: companyData.taxNumber,
          price: subscriptionInfo.price,
          frequency: `${subscriptionInfo.interval_count}_${subscriptionInfo.interval}`,
        },
      });
      const company = await createCompany();

      if (company === undefined) {
        snack.error(t('messages.COMPANY_CREATION_FAILS'));
        queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
        closeModal();
        setIsLoading(false);
        return;
      }
      try {
        const subscription = await createSubscription.mutateAsync(company.id);

        await navigateToSecondCompanyCustomerPortal({
          subscriptionId: subscription.id,
          returnUrl: `${window.location.origin}${RouteConfig.CompanyRedirect.buildLink({ id: company.id })}`,
        });
      } catch (error) {
        setIsLoading(false);
        captureException(error);
        snack.error(t('messages.COMPANY_CREATION_FAILS'));
      }
    }
  }, [
    captureException,
    closeModal,
    companyData.taxNumber,
    createCompany,
    createSubscription,
    navigateToSecondCompanyCustomerPortal,
    queryClient,
    subscriptionInfo,
    t,
    track,
  ]);

  const hasInvoicing =
    subscriptionInfo?.features.includes(FeatureFlag.Invoicing) ||
    subscriptionInfo?.features.includes(FeatureFlag.SeeInvoices);
  const availableFeatures = [
    t('summary.features.treasury'),
    ...(hasInvoicing ? [t('summary.features.invoicing')] : []),
  ].join(', ');

  const intervalContext = useMemo(() => {
    if (subscriptionInfo) {
      if (subscriptionInfo.interval === 'month') {
        return subscriptionInfo.interval_count > 1 ? 'MORE_MONTHS' : 'ONE_MONTH';
      }
      return subscriptionInfo.interval_count > 1 ? 'MORE_YEARS' : 'ONE_YEAR';
    }
  }, [subscriptionInfo]);

  return (
    <VStack gap={5}>
      <Text variant="body2" weight="regular">
        <Trans t={t} i18nKey={subscriptionInfo ? 'description_activate' : 'description_request'} />
      </Text>
      <VStack gap={2} className={styles.infoContainer}>
        <HStack gap={2}>
          <Text variant="body2" weight="regular" className={styles.title}>
            {t('summary.company')}
          </Text>
          <Text variant="body2" weight="semibold" className={styles.name}>
            {companyData.name}
          </Text>
        </HStack>
        <HStack gap={2}>
          <Text variant="body2" weight="regular" className={styles.title}>
            {t('summary.tax_code')}
          </Text>
          <Text variant="body2" weight="semibold">
            {companyData.taxNumber}
          </Text>
        </HStack>
        {subscriptionInfo && (
          <>
            <HStack gap={2}>
              <Text variant="body2" weight="regular" className={styles.title}>
                {t('summary.features.title')}
              </Text>
              <Text variant="body2" weight="semibold">
                {availableFeatures}
              </Text>
            </HStack>
            <HStack gap={2} align="center">
              <Text variant="body2" weight="regular" className={styles.title}>
                {t('summary.price.title')}
              </Text>
              <HStack gap={1} align="center">
                <Text variant="body2" weight="semibold">
                  <Trans components={{ s: <s /> }}>
                    {t('summary.price.value', {
                      price: fDecimalNumberTwoDecimal(subscriptionInfo.price),
                      discountedPrice: formatMoney(newMoney(subscriptionInfo.discounted_price)),
                    })}
                  </Trans>
                </Text>
                <div className={styles.chip}>
                  <Text variant="caption" weight="semibold">
                    {t('summary.discount')}
                  </Text>
                </div>
              </HStack>
            </HStack>
            <HStack gap={2}>
              <Text variant="body2" weight="regular" className={styles.title}>
                <Text variant="body2" weight="regular" className={styles.title}>
                  {t('summary.interval.title')}
                </Text>
              </Text>
              <Text variant="body2" weight="semibold">
                {t('summary.interval.value', {
                  context: intervalContext,
                  count: subscriptionInfo.interval_count,
                })}
              </Text>
            </HStack>
          </>
        )}
      </VStack>
      {!subscriptionInfo && (
        <AlertMessage type="info" box className={styles.alert}>
          <Trans t={t} i18nKey="alert_info" components={{ u: <u /> }} />
        </AlertMessage>
      )}
      <ModalActions className={styles.actionsContainer}>
        <Button
          variant={'outlined'}
          onClick={() => {
            closeModal();
          }}
        >
          {t('actions.cancel')}
        </Button>
        {subscriptionInfo ? (
          <Button onClick={handleCreateSubscription} Icon={IconArrowRight} loading={isLoading}>
            {t('actions.activate')}
          </Button>
        ) : (
          <Button onClick={handleCreateSubscription} Icon={IconSend} loading={isLoading}>
            {t('actions.request')}
          </Button>
        )}
      </ModalActions>
    </VStack>
  );
};

export default SubscribeCompany;
